import { useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  total: number;
  onClearAll: () => void;
  children: ReactNode;
}

/**
 * Mobile full-screen filter dialog. Focus-trapped, Esc-closeable, with a sticky
 * "Show {N} stays" CTA bound to the live result count.
 */
export function FilterSheet({ open, onClose, total, onClearAll, children }: FilterSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Filters"
      className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden"
      ref={panelRef}
    >
      <div className="flex items-center justify-between border-b border-sand px-4 py-3">
        <h2 className="font-serif text-xl font-bold text-ink">Filters</h2>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close filters"
          className="flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-cream-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">{children}</div>

      <div className="flex items-center gap-3 border-t border-sand px-4 py-3">
        <button type="button" onClick={onClearAll} className="text-sm font-semibold text-clay">
          Clear all
        </button>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded-full bg-clay px-6 py-3 font-semibold text-white transition hover:bg-clay-dark"
        >
          Show {total} hotels
        </button>
      </div>
    </div>
  );
}
